import * as React from 'react';
import { QuickSearchPalette } from '@/components/quick-search/quick-search-palette';
import type { PermissionCatalogEntry } from '@/lib/types';
import type { AuthUser } from '@/lib/types';
import type { ToolsVisibilityCapabilities } from '@/lib/tools-visibility';

type QuickSearchContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
};

const QuickSearchContext = React.createContext<QuickSearchContextValue | null>(null);

export function useQuickSearch(): QuickSearchContextValue {
  const value = React.useContext(QuickSearchContext);
  if (!value) {
    throw new Error('useQuickSearch must be used within QuickSearchProvider');
  }
  return value;
}

type QuickSearchProviderProps = {
  children: React.ReactNode;
  user: AuthUser | null;
  navEntries: PermissionCatalogEntry[];
  toolsCapabilities: ToolsVisibilityCapabilities;
  disabled?: boolean;
};

export function QuickSearchProvider({
  children,
  user,
  navEntries,
  toolsCapabilities,
  disabled = false,
}: QuickSearchProviderProps) {
  const [open, setOpen] = React.useState(false);

  const toggle = React.useCallback(() => {
    setOpen((current) => !current);
  }, []);

  React.useEffect(() => {
    if (disabled) return;
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [disabled]);

  const value = React.useMemo(
    () => ({ open, setOpen, toggle }),
    [open, toggle],
  );

  return (
    <QuickSearchContext.Provider value={value}>
      {children}
      {!disabled ? (
        <QuickSearchPalette
          open={open}
          onOpenChange={setOpen}
          user={user}
          navEntries={navEntries}
          toolsCapabilities={toolsCapabilities}
        />
      ) : null}
    </QuickSearchContext.Provider>
  );
}
